import React, { useState, useEffect, useRef } from 'react';
import { mlModelAPI, trainMLModel, modelRegistryAPI, previewDataset, runKMeansClustering, recommendClusterK, getClusterScatterUrl } from '../api';
import { DEFAULT_KNOWN_AD_GENES, parseKnownAdGenesInput } from '../constants/knownAdGenes';
//import VisualizationDisplay from './VisualizationDisplay';
import LoadingSpinner from './LoadingSpinner';
import { EmptyState, InfoTooltip, ErrorState } from './shared/UIComponents';
import { useActiveModel } from '../context/ActiveModelContext';
import TrainingCurves from './TrainingCurves';

function MLModelTraining({ datasets = [], datasetName, datasetId = null, modelType = 'random_forest', onModelTypeChange }) {
  const [selectedDataset, setSelectedDataset] = useState(datasetName || '');
  const [availableColumns, setAvailableColumns] = useState([]);
  const [targetColumn, setTargetColumn] = useState('');
  const [selectedModel, setSelectedModel] = useState(modelType);
  const [taskType, setTaskType] = useState('auto');
  const [testSize, setTestSize] = useState(0.2);
  const [normalize, setNormalize] = useState(true);
  const [normalizationMethod, setNormalizationMethod] = useState('zscore');
  const [datasetType, setDatasetType] = useState('');
  const [trainResult, setTrainResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [analysisMode, setAnalysisMode] = useState('supervised');
  const [nClusters, setNClusters] = useState(3);
  const [kRecommendation, setKRecommendation] = useState(null);
  const [recommendingK, setRecommendingK] = useState(false);
  const { setActiveModel } = useActiveModel();

  
  // Neural network hyperparameters (shown when model type is neural_network)
  const [tuningMode, setTuningMode] = useState('none');
  const [learningRate, setLearningRate] = useState(0.001);
  const [hiddenDimsPreset, setHiddenDimsPreset] = useState('64,32');
  const [dropoutRate, setDropoutRate] = useState(0.3);
  const [weightDecay, setWeightDecay] = useState(0.0001);
  const [maxEpochs, setMaxEpochs] = useState(10);
  const [earlyStoppingPatience, setEarlyStoppingPatience] = useState(3);
  // Classification: threshold for predicting positive (P(positive) >= threshold). Stored with model and used at prediction.
  const [decisionThreshold, setDecisionThreshold] = useState(0.5);

  // Known-disease gene list for validation (instance comparison / feature checks); sent to API as known_ad_genes
  const [knownAdGenesText, setKnownAdGenesText] = useState(() => DEFAULT_KNOWN_AD_GENES.join(', '));

  const NN_LAYER_PRESETS = {
    '64,32': [64, 32],
    '128,64,32': [128, 64, 32],
    '256,128': [256, 128],
    '512,256,128': [512, 256, 128],
    '512,256,128,64': [512, 256, 128, 64],
    '256,256,128': [256, 256, 128],
};

  const [isPolling, setIsPolling] = useState(false);
  const [trainingStatus, setTrainingStatus] = useState("idle");
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [liveHistory, setLiveHistory] = useState([]); // Array of {epoch, loss, val_loss, accuracy/mse}
  const [detectedTaskType, setDetectedTaskType] = useState(null); // Track task type for curves
  const pollingIntervalRef = useRef(null);

  // --- Polling Effect ---
  useEffect(() => {
    if (!isPolling) return;

    const pollStatus = async () => {
      try {
        // Call the backend API
        const data = await mlModelAPI.getTrainingStatus(selectedDataset, selectedModel);

        // Update the UI with live data
        setTrainingStatus(data.status);
        setProgress(data.progress || 0);
        setStatusMessage(data.message || 'Processing...');

        // Track task type from status message or infer from metrics
        if (data.current_epoch && !detectedTaskType) {
          // Try to infer task type from available metrics
          if (data.accuracy !== undefined) {
            setDetectedTaskType('classification');
          } else if (data.mse !== undefined) {
            setDetectedTaskType('regression');
          }
        }

        if (data.loss !== undefined && data.current_epoch) {
          setLiveHistory((prevHistory) => {
            if (prevHistory.length >= data.current_epoch) {
              return prevHistory;
            }
            const newHistory = [...prevHistory];
            newHistory.push({
              epoch: data.current_epoch,
              loss: data.loss,
              val_loss: data.val_loss !== undefined ? data.val_loss : null,
              accuracy: data.accuracy !== undefined ? data.accuracy : null,
              mse: data.mse !== undefined ? data.mse : null
            });
            return newHistory;
          });
        }
        
        if (data.status === 'completed') {
            clearInterval(pollingIntervalRef.current); // Stop the timer
            setIsPolling(false); // Update polling state
            setTrainResult(data.result); // Save the final data
            // Keep liveHistory - don't clear it, let TrainingCurves use it as fallback
            const trained = data?.result || {};
            const modelId = trained.model_registry_id || trained.model_id || trained.id || null;
            if (modelId) {
              try {
                const fullInfo = await modelRegistryAPI.getModelInfo(modelId);
                setActiveModel(fullInfo);
                // Update trainResult with full info if it has training_summary
                if (fullInfo.metadata?.training_summary) {
                  setTrainResult(prev => ({
                    ...prev,
                    metadata: {
                      ...prev?.metadata,
                      training_summary: fullInfo.metadata.training_summary
                    }
                  }));
                }
              } catch (e) {
                setActiveModel({
                  id: modelId,
                  dataset_name: trained.dataset_name || selectedDataset,
                  model_type: trained.model_type || selectedModel,
                  metrics: trained.metrics || null,
                  created_at: trained.created_at || null,
                });
              }
            }
        }
        if (data.status === 'failed') {
            clearInterval(pollingIntervalRef.current);
            setIsPolling(false);
            setError(`Training Failed: ${data.message}`);
        }

      } catch (err) {
        console.error("Polling error:", err);
      }
    };

    // Start the loop
    pollStatus(); // Run once immediately
    pollingIntervalRef.current = setInterval(pollStatus, 1000); // Then every 1000ms

    // Cleanup function (runs if component unmounts)
    return () => {
      if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
    };
  }, [isPolling, selectedDataset, selectedModel]);

  // Update selectedDataset when datasetName prop changes
  useEffect(() => {
    if (datasetName) {
      setSelectedDataset(datasetName);
    }
  }, [datasetName]);

  // Update selectedModel when modelType prop changes
  useEffect(() => {
    if (modelType) {
      setSelectedModel(modelType);
    }
  }, [modelType]);

// Fetch columns and auto-detect target column
  useEffect(() => {
    let isMounted = true;
    const fetchColumnsAndGuessTarget = async () => {
      if (!selectedDataset) {
        setAvailableColumns([]);
        setTargetColumn('');
        return;
      }
      try {
        const preview = await previewDataset(selectedDataset, 1);
        const columns = preview?.columns;
        if (Array.isArray(columns) && columns.length > 0) {
          if (isMounted) {
            setAvailableColumns(columns);

            // Smarter auto-detect logic
            const commonKeywords = ['target', 'label', 'class', 'status', 'condition', 'diagnosis', 'phenotype', 'is_AD', 'has_AD', 'outcome', 'response', 'rank', 'score', 'result', 'prediction', 'dependent_variable'];
            const colsLower = columns.map(c => String(c).toLowerCase());

            let guessedTarget = null;
            for (let keyword of commonKeywords) {
              const matchIndex = colsLower.findIndex(c => c.includes(keyword));
              if (matchIndex !== -1) {
                guessedTarget = columns[matchIndex];
                break;
              }
            }

            // Fallback to the last column if no keywords match
            if (!guessedTarget) {
              guessedTarget = columns[columns.length - 1];
            }

            setTargetColumn(String(guessedTarget));
          }
        }
      } catch (e) {
        // Silently ignore preview errors; user can still type target manually
        if (isMounted) setAvailableColumns([]);
      }
    };
    fetchColumnsAndGuessTarget();
    return () => { isMounted = false; };
  }, [selectedDataset]);



  const handleTrainModel = async () => {
    if (!selectedDataset || !targetColumn) {
      setError('Please select a dataset and enter a target column.');
      return;
    }
    setError(null);
    setTrainResult(null);
    setProgress(0);
    setLiveHistory([]); // Clear live history when starting new training
    setDetectedTaskType(null); // Reset task type
    setTrainingStatus("initializing");
    setStatusMessage("Initializing training...");
    setIsPolling(true); // Start polling for status updates

    try {
      let body = null;
      if (selectedModel === 'neural_network') {
        const hiddenDims = NN_LAYER_PRESETS[hiddenDimsPreset] || [128, 64, 32];
        const dropoutRates = hiddenDims.map(() => dropoutRate);
        body = { tuning_mode: tuningMode };
        const useCustomHyperparams = tuningMode === 'guided' || (
          tuningMode === 'none' &&
          (learningRate !== 0.001 || hiddenDimsPreset !== '64,32' || dropoutRate !== 0.3 ||
            weightDecay !== 0.0001 || maxEpochs !== 10 || earlyStoppingPatience !== 3)
        );
        if (useCustomHyperparams) {
          body.neural_network_hyperparameters = {
            learning_rate: learningRate,
            hidden_dims: hiddenDims,
            dropout_rates: dropoutRates,
            weight_decay: weightDecay,
            max_epochs: maxEpochs,
            early_stopping_patience: earlyStoppingPatience,
          };
        }
      }
      // For classification (or auto), send decision threshold so the model knows the cutoff for positive/negative at prediction time
      if (taskType === 'classification' || taskType === 'auto') {
        if (!body) body = {};
        body.decision_threshold = decisionThreshold;
      }

      const parsedGenes = parseKnownAdGenesInput(knownAdGenesText);
      if (parsedGenes.length > 0) {
        if (!body) body = {};
        body.known_ad_genes = parsedGenes;
      }

      const result = await trainMLModel(
        selectedDataset,
        targetColumn,
        selectedModel,
        testSize,
        normalize,
        normalizationMethod,
        taskType,
        datasetType,
        body,
        datasetId
      );
      
      // Update the parent component's model type
      if (onModelTypeChange) {
        onModelTypeChange(selectedModel);
      }
    } catch (err) {
      console.error("Training error:", err);
      setError(err.message || 'Failed to train model. Please check your parameters and try again.');
      setIsPolling(false); // Stop polling on error
    } finally {
      setLoading(false);
    }
  };

const handleRunUnsupervised = async () => {
    if (!selectedDataset) { 
      setError('Please select a dataset.'); 
      return; 
    }
    
    setError(null); 
    setTrainResult(null); 
    setLoading(true);
    
    try {
      const result = await runKMeansClustering(selectedDataset, nClusters);
      
      const dataToSet = result.data ? result.data : result;
      setTrainResult(dataToSet);
      console.log("KMeans Result:", dataToSet);

      if (onModelTypeChange) onModelTypeChange('k_means');
    } catch (err) {
      setError(err.message || 'Failed to run clustering workflow.');
    } finally { 
      setLoading(false); 
    }
  };

  const handleRecommendK = async () => {
    if (!selectedDataset) {
      setError('Please select a dataset.');
      return;
    }
    setError(null);
    setKRecommendation(null);
    setRecommendingK(true);
    try {
      const result = await recommendClusterK(selectedDataset, 2, 10);
      const data = result.data ? result.data : result;
      setKRecommendation(data);
      // Pre-fill the K input with the recommendation so the user can run it directly
      if (data && typeof data.recommended_k === 'number') {
        setNClusters(data.recommended_k);
      }
    } catch (err) {
      setError(err.message || 'Failed to recommend number of clusters.');
    } finally {
      setRecommendingK(false);
    }
  };

  // Helper for Status Badge Color
  const getStatusColor = () => {
    if (trainingStatus === 'failed') return '#ef4444'; // Red
    if (trainingStatus === 'completed') return '#10b981'; // Green
    return '#3b82f6'; // Blue
  };

  const handleDownloadCsv = () => {
    if (!trainResult || !trainResult.cluster_csv_data) return;

    // Convert the string into a Blob
    const blob = new Blob([trainResult.cluster_csv_data], { type: 'text/csv;charset=utf-8;' });
    
    // Create a temporary URL for the Blob
    const url = URL.createObjectURL(blob);
    
    // Create a hidden anchor tag and trigger the download
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${selectedDataset}_clusters.csv`);
    document.body.appendChild(link);
    link.click();
    
    // Clean up the DOM and memory
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="ml-training">
      <div className="ml-training__header">
        <InfoTooltip
          title="ML Model Training"
          content="Train machine learning models on your dataset. Choose your dataset, target variable, model type, and training parameters."
        >
          <h2 className="ml-training__title">ML Model Training</h2>
        </InfoTooltip>
        
        <div style={{ marginBottom: 12, padding: 10, border: '1px solid #dbeafe', borderRadius: 8, background: '#f8fbff', fontSize: 13 }}>
          <strong>Training guidance:</strong> choose task/model first, then set threshold (classification only) and hyperparameters. Outputs include metrics, training status, and model artifacts for downstream validation/interpretation/export.
        </div>
        
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', background: '#f1f5f9', padding: '6px', borderRadius: '8px' }}>
          <button onClick={() => setAnalysisMode('supervised')} style={{ flex: 1, padding: '10px', background: analysisMode === 'supervised' ? '#ffffff' : 'transparent', fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Supervised (Predictive)</button>
          <button onClick={() => setAnalysisMode('unsupervised')} style={{ flex: 1, padding: '10px', background: analysisMode === 'unsupervised' ? '#ffffff' : 'transparent', fontWeight: 'bold', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>Unsupervised (Exploratory)</button>
        </div>

        {datasets.length === 0 ? (
          <EmptyState message="No datasets available. Please upload or explore datasets first." icon="📁" />
        ) : (
          <>
            <div className="ml-training__form-group">
              <label className="ml-training__form-label">Dataset:</label>
              <select value={selectedDataset} onChange={(e) => setSelectedDataset(e.target.value)} className="ml-training__form-select" disabled={isPolling || loading}>
                <option value="">Select dataset</option>
                {datasets.map((dataset, index) => (
                  <option key={index} value={dataset}>{dataset}</option>
                ))}
              </select>
            </div>

            {/* SUPERVISED CONTROLS */}
            {analysisMode === 'supervised' && (
              <>
                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Dataset Type:</label>
                  <select value={datasetType} onChange={(e) => setDatasetType(e.target.value)} className="ml-training__form-select" disabled={isPolling || loading}>
                    <option value="">Auto-detect</option>
                    <option value="proteomic">Proteomics</option>
                    <option value="transcriptomic">Transcriptomics</option>
                  </select>
                </div>

                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Target Column:</label>
                  {availableColumns.length > 0 ? (
                    <select
                      value={targetColumn}
                      onChange={(e) => setTargetColumn(e.target.value)}
                      className="ml-training__form-select"
                      disabled={isPolling || loading}
                    >
                      <option value="">-- Select a target column --</option>
                      {availableColumns.map((col, idx) => (
                        <option key={idx} value={col}>{col}</option>
                      ))}
                    </select>
                  ) : (
                    <input type="text" value={targetColumn} onChange={(e) => setTargetColumn(e.target.value)} placeholder="e.g. target" className="ml-training__form-input" disabled={isPolling || loading} />
                  )}
                </div>

                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Task Type:</label>
                  <select value={taskType} onChange={(e) => setTaskType(e.target.value)} className="ml-training__form-select" disabled={loading}>
                    <option value="auto">Auto</option>
                    <option value="classification">Classification</option>
                    <option value="regression">Regression</option>
                  </select>
                </div>

                {(taskType === 'classification' || taskType === 'auto') && (
                  <div className="ml-training__form-group">
                    <label className="ml-training__form-label">Decision threshold (positive class):</label>
                    <input type="number" step="0.05" min="0" max="1" value={decisionThreshold} onChange={(e) => setDecisionThreshold(Number(e.target.value))} className="ml-training__form-input" disabled={isPolling || loading} style={{ width: '80px' }} />
                  </div>
                )}

                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Model Type:</label>
                  <select
                    value={selectedModel}
                    onChange={(e) => {
                      setSelectedModel(e.target.value);
                      if (onModelTypeChange) onModelTypeChange(e.target.value);
                    }}
                    className="ml-training__form-select"
                    disabled={isPolling || loading}
                  >
                    <option value="random_forest">Random Forest</option>
                    <option value="logistic_regression">Logistic Regression</option>
                    <option value="svm">SVM</option>
                    <option value="neural_network">Neural Network (PyTorch)</option>
                    <option value="linear">Linear Regression</option>
                  </select>
                </div>

                {selectedModel === 'neural_network' && (
                  <div className="ml-training__nn-options" style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
                    <h4 style={{ marginTop: 0, marginBottom: '12px', fontSize: '1rem' }}>Neural network options</h4>
                    <div className="ml-training__form-group">
                      <label className="ml-training__form-label">Tuning:</label>
                      <select value={tuningMode} onChange={(e) => setTuningMode(e.target.value)} className="ml-training__form-select" disabled={isPolling || loading}>
                        <option value="none">None (defaults or custom below)</option>
                        <option value="guided">Guided (use custom hyperparameters)</option>
                      </select>
                    </div>
                    {/* Additional NN params can remain here */}
                    <div className="ml-training__form-group">
                      <label className="ml-training__form-label">Learning rate:</label>
                      <input type="number" step="0.0001" min="0.00001" max="0.1" value={learningRate} onChange={(e) => setLearningRate(Number(e.target.value))} className="ml-training__form-input" disabled={isPolling || loading} />
                    </div>
                    <div className="ml-training__form-group">
                      <label className="ml-training__form-label">Hidden layer sizes:</label>
                      <select value={hiddenDimsPreset} onChange={(e) => setHiddenDimsPreset(e.target.value)} className="ml-training__form-select" disabled={isPolling || loading}>
                        <option value="64,32">64 → 32 (Faster, less memory)</option>
                        <option value="128,64,32">128 → 64 → 32 </option>
                        <option value="256,128">256 → 128</option>
                      </select>
                    </div>
                  </div>
                )}

                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Test Size:</label>
                  <input type="number" step="0.01" min="0.05" max="0.5" value={testSize} onChange={(e) => setTestSize(Number(e.target.value))} className="ml-training__form-input" disabled={isPolling || loading} />
                </div>
                <div className="ml-training__form-group">
                  <label className="ml-training__form-label">Normalization  :</label>
                  <select value={normalizationMethod} onChange={(e) => setNormalizationMethod(e.target.value)} className="ml-training__form-select" disabled={isPolling || loading}>
                    <option value="zscore">Z-Score</option>
                    <option value="minmax">Min-Max</option>
                    <option value="robust">Robust</option>
                    <option value="log2">Log2</option>
                    <option value="none">None</option>
                  </select>
                </div>
              </>
            )}

            {/* UNSUPERVISED CONTROLS */}
            {analysisMode === 'unsupervised' && (
              <div className="ml-training__form-group" style={{ marginTop: '15px' }}>
                <label className="ml-training__form-label">Number of Clusters (k):</label>
                <input
                  type="number"
                  min="2"
                  max="20"
                  value={nClusters}
                  onChange={(e) => setNClusters(parseInt(e.target.value, 10))}
                  className="ml-training__form-input"
                  disabled={loading}
                />
                <small className="ml-training__hint">The number of distinct groups to partition your data into.</small>

                <button
                  type="button"
                  onClick={handleRecommendK}
                  disabled={recommendingK || loading}
                  style={{ marginTop: '10px', padding: '8px 14px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe', borderRadius: '6px', cursor: recommendingK ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.85rem' }}
                >
                  {recommendingK ? 'Analyzing K…' : 'Recommend K (elbow + silhouette)'}
                </button>

                {kRecommendation && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#f8fbff', border: '1px solid #bfdbfe', borderRadius: '8px', fontSize: '0.85rem' }}>
                    <div style={{ display: 'flex', gap: '20px', marginBottom: '8px' }}>
                      <div>
                        <div style={{ color: '#64748b', fontWeight: 600 }}>Recommended K</div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1e3a8a' }}>{kRecommendation.recommended_k ?? '—'}</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>max silhouette</div>
                      </div>
                      <div>
                        <div style={{ color: '#64748b', fontWeight: 600 }}>Elbow K</div>
                        <div style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#1e3a8a' }}>{kRecommendation.elbow_k ?? '—'}</div>
                        <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>inertia curve</div>
                      </div>
                    </div>
                    {kRecommendation.interpretation && (
                      <div style={{ color: '#334155', marginBottom: '8px' }}>{kRecommendation.interpretation}</div>
                    )}
                    {Array.isArray(kRecommendation.curve) && (
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead>
                          <tr style={{ color: '#64748b', textAlign: 'left' }}>
                            <th style={{ padding: '3px 6px' }}>K</th>
                            <th style={{ padding: '3px 6px' }}>Silhouette</th>
                            <th style={{ padding: '3px 6px' }}>Inertia</th>
                          </tr>
                        </thead>
                        <tbody>
                          {kRecommendation.curve.map((row) => (
                            <tr key={row.k} style={{ background: row.k === kRecommendation.recommended_k ? '#dbeafe' : 'transparent' }}>
                              <td style={{ padding: '3px 6px' }}>{row.k}</td>
                              <td style={{ padding: '3px 6px' }}>{typeof row.silhouette_score === 'number' ? row.silhouette_score.toFixed(4) : '—'}</td>
                              <td style={{ padding: '3px 6px' }}>{typeof row.inertia === 'number' ? row.inertia.toFixed(1) : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ACTION BUTTONS */}
            {!isPolling && analysisMode === 'supervised' && (
              <button onClick={handleTrainModel} className="ml-training__train-btn">
                Train Model
              </button>
            )}
            
            {analysisMode === 'unsupervised' && (
              <button onClick={handleRunUnsupervised} className="ml-training__train-btn" disabled={loading}>
                {loading ? 'Running Clustering...' : 'Run Clustering'}
              </button>
            )}
          </> 
        )}

        {/* PROGRESS POLLING */}
        {isPolling && (
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px' }}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '10px'}}>
                <span style={{fontWeight: 'bold', textTransform: 'uppercase', color: '#64748b'}}>{trainingStatus}</span>
                <span style={{fontWeight: 'bold'}}>{progress}%</span>
            </div>
            <div style={{width: '100%', height: '12px', backgroundColor: '#e2e8f0', borderRadius: '6px', overflow: 'hidden'}}>
                <div style={{ width: `${Math.max(5, progress)}%`, height: '100%', backgroundColor: getStatusColor(), transition: 'width 0.5s ease' }}></div>
            </div>
            <div style={{marginTop: '10px', fontFamily: 'monospace', fontSize: '0.9rem', color: '#334155'}}>{statusMessage}</div>
            
            {selectedModel === 'neural_network' ? (
              <div style={{ marginTop: '20px' }}>
                <TrainingCurves liveHistory={liveHistory} taskType={detectedTaskType || taskType} />
              </div>
            ) : (
              <div style={{ marginTop: '20px', height: '150px', border: '2px dashed #cbd5e1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', backgroundColor: '#fff' }}>
                <div style={{textAlign: 'center'}}>
                  <div style={{fontSize: '24px', marginBottom: '5px'}}>⏳</div>
                  <div style={{fontSize: '0.8rem'}}>Visualizations generated after training</div>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <ErrorState message={error} onRetry={() => { setError(null); analysisMode === 'supervised' ? handleTrainModel() : handleRunUnsupervised(); }} />}

        {/* SUPERVISED RESULTS */}
        {trainResult && analysisMode === 'supervised' && (
          <div className="ml-training__results">
            <h3 className="ml-training__results-title">Training Result</h3>
            <div style={{ marginBottom: 10, fontSize: 13, color: '#556' }}>Output interpretation: review task-specific metrics, convergence behavior, and model registry ID.</div>
            {trainResult.model_type === 'neural_network' && (
                <div style={{ marginBottom: '20px' }}>
                    <TrainingCurves trainingSummary={trainResult.metadata?.training_summary} liveHistory={liveHistory.length > 0 ? liveHistory : undefined} taskType={trainResult.metadata?.training_summary?.task_type || detectedTaskType || taskType} />
                </div>
            )}
            <pre className="ml-training__results-json">{JSON.stringify(trainResult, null, 2)}</pre>
          </div>
        )}

        {/* UNSUPERVISED RESULTS - FORMATTED */}
        {trainResult && analysisMode === 'unsupervised' && (
          <div className="ml-training__results" style={{ marginTop: '30px' }}>
            <h3 className="ml-training__results-title" style={{ borderBottom: '2px solid #e2e8f0', paddingBottom: '10px', marginBottom: '20px' }}>
              Clustering Results
            </h3>

            {/* Metrics Dashboard */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginBottom: '25px' }}>
              {typeof trainResult.silhouette_score === 'number' && (
                <div style={{ padding: '15px', background: '#f8fbff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.9rem', marginBottom: '5px' }}>Silhouette Score</div>
                  <div style={{ fontSize: '1.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>{trainResult.silhouette_score.toFixed(4)}</div>
                </div>
              )}
              {trainResult.inertia !== undefined && (
                <div style={{ padding: '15px', background: '#f8fbff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.9rem', marginBottom: '5px' }}>Inertia</div>
                  <div style={{ fontSize: '1.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>{trainResult.inertia.toFixed(2)}</div>
                </div>
              )}
              {typeof trainResult.quality_metrics?.calinski_harabasz_score === 'number' && (
                <div style={{ padding: '15px', background: '#f8fbff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.9rem', marginBottom: '5px' }}>Calinski-Harabasz</div>
                  <div style={{ fontSize: '1.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>{trainResult.quality_metrics.calinski_harabasz_score.toFixed(1)}</div>
                  <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>higher = better</div>
                </div>
              )}
              {typeof trainResult.quality_metrics?.davies_bouldin_score === 'number' && (
                <div style={{ padding: '15px', background: '#f8fbff', borderRadius: '8px', border: '1px solid #bfdbfe', textAlign: 'center' }}>
                  <div style={{ fontWeight: '600', color: '#1e40af', fontSize: '0.9rem', marginBottom: '5px' }}>Davies-Bouldin</div>
                  <div style={{ fontSize: '1.8rem', color: '#1e3a8a', fontWeight: 'bold' }}>{trainResult.quality_metrics.davies_bouldin_score.toFixed(3)}</div>
                  <div style={{ color: '#94a3b8', fontSize: '0.72rem' }}>lower = better</div>
                </div>
              )}
            </div>

            {/* Plain-language interpretation of cluster separation */}
            {trainResult.quality_metrics?.interpretation && (
              <div style={{ marginBottom: '20px', padding: '12px 15px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '8px', color: '#0c4a6e', fontSize: '0.9rem' }}>
                <strong>Cluster quality:</strong> {trainResult.quality_metrics.interpretation}
              </div>
            )}

            {/* Visualizations — same pattern as VisualizationDisplay: dedicated /visualizations/ endpoint */}
            {(trainResult.plot_path || selectedDataset) && (
               <div style={{ marginBottom: '25px', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px', background: '#ffffff', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                 <h4 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: '#334155' }}>Cluster Scatter Plot (PCA projection)</h4>
                 <img
                    key={`cluster-scatter-${selectedDataset}-${Date.now()}`}
                    src={getClusterScatterUrl(selectedDataset)}
                    alt="K-Means cluster scatter plot"
                    style={{ width: '50%', height: 'auto', borderRadius: '6px', border: '1px solid #f1f5f9' }}
                    onError={(e) => { e.target.style.display = 'none'; e.target.insertAdjacentHTML('afterend', '<p style="color:#ef4444; font-size:14px;">Cluster plot not found. Re-run clustering or check that plot generation is enabled (not in staging mode).</p>'); }}
                 />
               </div>
            )}

            {/* Cluster Distribution & Details Table */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Left Column: Distribution */}
              {trainResult.cluster_distribution && (
                <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                  <h4 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: '#334155' }}>Cluster Distribution</h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {Object.entries(trainResult.cluster_distribution).map(([cluster, count]) => (
                      <div key={cluster} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #f1f5f9' }}>
                        <span style={{ fontWeight: '600', color: '#64748b' }}>{cluster.replace('_', ' ').toUpperCase()}</span>
                        <span style={{ fontWeight: 'bold', color: '#8b5cf6' }}>{count} samples</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Right Column: Execution Details */}
              <div style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '15px' }}>
                <h4 style={{ margin: '0 0 15px 0', fontSize: '1.1rem', color: '#334155' }}>Execution Details</h4>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                  <tbody>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Algorithm</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#0f172a' }}>K-Means</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#64748b', fontWeight: '500' }}>K Value</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#0f172a' }}>{nClusters}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Dataset</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#0f172a' }}>{selectedDataset}</td>
                    </tr>
                    <tr>
                      <td style={{ padding: '8px 0', color: '#64748b', fontWeight: '500' }}>Output File</td>
                      <td style={{ padding: '8px 0', textAlign: 'right', color: '#0f172a', wordBreak: 'break-all', fontSize: '0.8rem' }}>{trainResult.output_file || 'N/A'}</td>
                    </tr>
                  </tbody>
                </table>
              </div> 
              {trainResult.cluster_csv_data && (
              <div style={{ marginTop: '25px', display: 'flex', justifyContent: 'center' }}>
                <button
                  onClick={handleDownloadCsv}
                  style={{
                    backgroundColor: '#10b981', 
                    color: '#ffffff',
                    padding: '12px 24px',
                    border: 'none',
                    borderRadius: '6px',
                    fontWeight: 'bold',
                    fontSize: '1rem',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  Download Gene Clusters (CSV)
                </button>
              </div>
            )}
            </div>
          </div>  
        )}
      </div>
    </div>
  );

}

export default MLModelTraining;